import React, { useState, useEffect } from 'react';
import ProductsView from './ProductsView';
import ProductEditForm from './ProductEditForm';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';
import { useShopFilter } from '../shops/hooks/useShopFilter';
import ShopsSidebar from './components/ShopsSidebar';
import { Skeleton } from '@/components/ui/skeleton';
import LoadingSpinner from './components/LoadingSpinner';

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState<string>('list');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const { isSubscriptionActive, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  
  const searchParams = new URLSearchParams(location.search);
  const urlShopId = searchParams.get('shopId');
  const shopId = urlShopId || localStorage.getItem('selectedShopId') || undefined;
  
  console.log("Products component rendering with shopId:", shopId);
  console.log("Current page:", currentPage);
  console.log("Selected product ID:", selectedProductId);
  console.log("Subscription status:", isSubscriptionActive ? "Active" : "Inactive");
  
  const {
    shops,
    isCollapsed,
    setIsCollapsed,
    filteredShops,
    searchTerm,
    setSearchTerm,
    isLoading: shopsLoading,
    isSingleSelectionMode,
    selectedShops,
    singleSelectedShop,
    toggleShopSelection,
    setSingleSelectedShop,
    refreshShops
  } = useShopFilter();
  
  useEffect(() => {
    if (urlShopId && urlShopId !== singleSelectedShop) {
      console.log("Syncing shop ID from URL:", urlShopId);
      setSingleSelectedShop(urlShopId);
    }
  }, [urlShopId, singleSelectedShop, setSingleSelectedShop]);
  
  const handlePageChange = (pageId: string) => {
    console.log("Products: handlePageChange called with pageId:", pageId);
    
    if (pageId.includes('?shopId=')) {
      const [basePage, queryString] = pageId.split('?');
      setCurrentPage(basePage);
      
      if (basePage === 'new') {
        const shopIdParam = new URLSearchParams('?' + queryString).get('shopId');
        console.log("Creating new product for shop:", shopIdParam);
        setSelectedProductId('new');
        navigate(`/dashboard?page=products&view=new&shopId=${shopIdParam}`, { replace: true });
      } else {
        setSelectedProductId(basePage);
        navigate(`/dashboard?page=products&view=${basePage}`, { replace: true });
      }
    } else {
      console.log("Navigating to page:", pageId);
      setCurrentPage(pageId);
      
      if (pageId !== 'list') {
        setSelectedProductId(pageId);
        navigate(`/dashboard?page=products&view=${pageId}${shopId ? `&shopId=${shopId}` : ''}`, { replace: true });
      } else {
        setSelectedProductId(null);
        navigate('/dashboard?page=products', { replace: true });
      }
    }
  };
  
  const handleBackToList = () => {
    console.log("Navigating back to products list");
    setCurrentPage('list');
    setSelectedProductId(null);
    navigate('/dashboard?page=products', { replace: true });
  };
  
  const handleSuccess = () => {
    console.log("Product saved successfully, returning to list");
    handleBackToList();
  };
  
  const renderShopsSidebar = () => {
    return (
      <ShopsSidebar
        shops={shops}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isSingleSelectionMode={isSingleSelectionMode}
        selectedShops={selectedShops}
        singleSelectedShop={singleSelectedShop}
        toggleShopSelection={toggleShopSelection}
        setSingleSelectedShop={setSingleSelectedShop}
        filterProductsByShops={() => {}}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleAddProduct={() => {}}
        isLoading={shopsLoading}
        refreshShops={refreshShops}
      />
    );
  };
  
  if (isSubscriptionLoading || isSubscriptionActive === null) {
    return (
      <div className="flex h-full">
        {renderShopsSidebar()}
        <div className="flex-1 h-full bg-pastel-blueLight dark:bg-[#121624] p-6">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
  
  if (currentPage === 'new' || (currentPage !== 'list' && selectedProductId)) {
    const productId = currentPage === 'new' ? 'new' : selectedProductId || '';
    console.log("Rendering ProductEditForm with productId:", productId, "and shopId:", shopId);
    
    return (
      <ProductEditForm
        productId={productId}
        onBack={handleBackToList}
        onSuccess={handleSuccess}
        shopId={shopId || undefined}
      />
    );
  }
  
  if (isSubscriptionActive === false) {
    return (
      <div className="flex h-full">
        {renderShopsSidebar()}
        <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
          <SubscriptionLock 
            title="Produits verrouillés"
            description="La gestion de produits n'est disponible qu'avec un abonnement actif."
            buttonText="Débloquer la gestion des produits"
          />
        </div>
      </div>
    );
  }
  
  return <ProductsView handlePageChange={handlePageChange} />;
};

export default Products;
